import React from 'react';

/**
 * 骨架屏：替代整页「转圈圈」。用微光(shimmer)灰块勾出页面大致结构，
 * 让用户一进页面就看到"内容轮廓"而不是空白转圈，感知加载更快。
 *
 * 用法：
 *   if (loading) return <ListSkeleton rows={6} />;       // 列表页(订单/委托/通知)
 *   if (loading) return <GridSkeleton count={6} />;      // 卡片网格(浏览/收藏)
 *   if (loading) return <DetailSkeleton />;              // 详情页(拍品详情)
 *   if (loading) return <PageSkeleton />;                // 通用兜底
 */

const BG = '#FAF8F5';
const BASE = '#ECE7DE';   // 灰块底色
const HI = '#F6F2EB';     // 微光高亮

const KF_ID = 'yc-skeleton-kf';
function ensureKeyframes() {
  if (typeof document === 'undefined' || document.getElementById(KF_ID)) return;
  const el = document.createElement('style');
  el.id = KF_ID;
  el.textContent = `@keyframes yc-shimmer { 0% { background-position: -420px 0; } 100% { background-position: 420px 0; } }`;
  document.head.appendChild(el);
}

// 单个微光灰块
export function Bar({ w = '100%', h = 14, r = 6, style }) {
  ensureKeyframes();
  return (
    <div style={{
      width: w, height: h, borderRadius: r,
      background: `linear-gradient(90deg, ${BASE} 25%, ${HI} 50%, ${BASE} 75%)`,
      backgroundSize: '840px 100%',
      animation: 'yc-shimmer 1.3s ease-in-out infinite',
      ...style,
    }} />
  );
}

const cardStyle = { background: '#fff', borderRadius: 12, padding: 14, border: '1px solid #EFE9DF' };

// 列表页：头像 + 两行文字，重复 rows 行
export function ListSkeleton({ rows = 6 }) {
  return (
    <div style={{ background: BG, minHeight: '60vh', padding: '14px 16px' }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ ...cardStyle, display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10 }}>
          <Bar w={48} h={48} r={10} />
          <div style={{ flex: 1 }}>
            <Bar w="62%" h={14} style={{ marginBottom: 10 }} />
            <Bar w="40%" h={12} />
          </div>
          <Bar w={54} h={26} r={6} />
        </div>
      ))}
    </div>
  );
}

// 卡片网格：图片 + 标题 + 价格
export function GridSkeleton({ count = 6 }) {
  return (
    <div style={{ background: BG, minHeight: '60vh', padding: '14px 16px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
            <Bar w="100%" h={150} r={0} />
            <div style={{ padding: 12 }}>
              <Bar w="90%" h={13} style={{ marginBottom: 8 }} />
              <Bar w="60%" h={13} style={{ marginBottom: 12 }} />
              <Bar w="45%" h={16} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// 详情页：主图 + 标题 + 价格 + 按钮
export function DetailSkeleton() {
  return (
    <div style={{ background: BG, minHeight: '80vh', padding: '14px 16px' }}>
      <Bar w="100%" h={280} r={12} style={{ marginBottom: 16 }} />
      <Bar w="80%" h={20} style={{ marginBottom: 12 }} />
      <Bar w="55%" h={16} style={{ marginBottom: 20 }} />
      <div style={{ ...cardStyle, marginBottom: 12 }}>
        <Bar w="40%" h={14} style={{ marginBottom: 12 }} />
        <Bar w="70%" h={22} style={{ marginBottom: 14 }} />
        <Bar w="100%" h={44} r={8} />
      </div>
      <Bar w="100%" h={80} r={12} />
    </div>
  );
}

// 通用兜底
export default function PageSkeleton() {
  return (
    <div style={{ background: BG, minHeight: '60vh', padding: '20px 16px' }}>
      <Bar w="50%" h={22} style={{ marginBottom: 18 }} />
      <div style={{ ...cardStyle, marginBottom: 12 }}>
        <Bar w="80%" h={14} style={{ marginBottom: 10 }} />
        <Bar w="60%" h={14} style={{ marginBottom: 10 }} />
        <Bar w="70%" h={14} />
      </div>
      <div style={{ ...cardStyle }}>
        <Bar w="70%" h={14} style={{ marginBottom: 10 }} />
        <Bar w="50%" h={14} />
      </div>
    </div>
  );
}
